import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDevice, useUpdateDevice } from '../../../hooks/admin/useDevices';
import { useGroups } from '../../../hooks/admin/useGroups';
import { useUsers } from '../../../hooks/admin/useUsers';
import type { DeviceUpdateRequest } from '../../../types/admin';
import { useEffect, useMemo, useState } from 'react';

export default function DeviceEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const deviceId = parseInt(id || '0');

  const { data: device, isLoading } = useDevice(deviceId);
  const { data: groups } = useGroups();
  const { data: users } = useUsers();
  const updateDevice = useUpdateDevice();

  const partners = useMemo(
    () => (users ?? []).filter((u) => u.role === 'partner'),
    [users],
  );

  const { register, handleSubmit, watch, reset } = useForm<DeviceUpdateRequest & { partnerUserIdRaw: string }>({
    values: device ? {
      sigfoxRegistered: device.sigfoxRegistered,
      locationId: device.locationId,
      description: device.description,
      partnerUserIdRaw: device.partnerUserId == null ? '' : String(device.partnerUserId),
    } as any : undefined,
  });

  // Track form changes
  const [hasChanges, setHasChanges] = useState(false);
  const formValues = watch();

  useEffect(() => {
    if (!device) return;

    const currentPartner = device.partnerUserId == null ? '' : String(device.partnerUserId);
    const changed =
      formValues.sigfoxRegistered !== device.sigfoxRegistered ||
      formValues.locationId !== device.locationId ||
      formValues.description !== device.description ||
      (formValues as any).partnerUserIdRaw !== currentPartner;

    setHasChanges(changed);
  }, [formValues, device]);

  const onSubmit = async (raw: DeviceUpdateRequest & { partnerUserIdRaw: string }) => {
    try {
      const { partnerUserIdRaw, ...rest } = raw as any;
      const data: DeviceUpdateRequest = {
        ...rest,
        partnerUserId: partnerUserIdRaw === '' ? null : Number(partnerUserIdRaw),
      };
      await updateDevice.mutateAsync({ id: deviceId, data });
      reset(raw);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to update device:', error);
    }
  };

  if (isLoading) return <div>Loading device...</div>;
  if (!device) return <div>Device not found</div>;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Edit RIC</h1>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Serial Number:</span>
            <span className="ml-2 font-medium">{device.serialNumber}</span>
          </div>
          <div>
            <span className="text-gray-600">Date Code:</span>
            <span className="ml-2 font-medium">{device.dateCode}</span>
          </div>
          <div>
            <span className="text-gray-600">Hardware:</span>
            <span className="ml-2 font-medium">{device.hardwareVersion}</span>
          </div>
          <div>
            <span className="text-gray-600">Sigfox ID:</span>
            <span className="ml-2 font-medium">{device.sigfoxId}</span>
          </div>
          <div>
            <span className="text-gray-600">Site Name:</span>
            <span className="ml-2 font-medium">
              {device.siteName || <span className="text-gray-400">—</span>}
            </span>
          </div>
          <div className="col-span-2">
            <span className="text-gray-600">Sigfox PAC:</span>
            <span className="ml-2 font-medium">{device.sigfoxPac}</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                {...register('sigfoxRegistered')}
                className="mr-2"
              />
              <span>Sigfox Registered</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Group</label>
            <select
              {...register('locationId', { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value={0}>none</option>
              {groups?.map((group) => (
                <option key={group.locationId} value={group.locationId}>
                  [{group.locationId}] {group.locationName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Partner</label>
            <select
              {...register('partnerUserIdRaw' as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">— None —</option>
              {partners.map((p) => {
                const label =
                  [p.firstName, p.lastName].filter(Boolean).join(' ').trim()
                  || p.accountId
                  || p.email;
                return (
                  <option key={p.userId} value={String(p.userId)}>
                    {label} ({p.email})
                  </option>
                );
              })}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Owner partner who can manage this RIC and assign it to a customer.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              {...register('description')}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="flex gap-4 justify-end">
            <button
              type="submit"
              disabled={updateDevice.isPending || !hasChanges}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateDevice.isPending ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/devices')}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
